import { z } from 'zod/v4';
import { createSelectSchema, createInsertSchema } from 'drizzle-zod';

import { schema } from '@edgevanta/db/drizzle/client';

export const slugSchema = z.string().regex(/^[a-z0-9-]+$/, {
  message: 'Slug must contain only lowercase letters, numbers, and hyphens',
});

const customBidTabsMonthlySpendCeilingUsdInputSchema = z
  .union([
    z
      .number()
      .finite()
      .nonnegative(
        'Custom Bid Tabs monthly spend ceiling must be 0 or greater',
      ),
    z.string().regex(/^\d+(?:\.\d+)?$/, {
      message: 'Custom Bid Tabs monthly spend ceiling must be 0 or greater',
    }),
  ])
  .transform((value) => (typeof value === 'number' ? String(value) : value));

export const selectOrganizationSchema = createSelectSchema(
  schema.organizations,
  {
    logo: z.string().nullable(),
    metadata: z.string().nullable(),
    domains: z.array(z.string()).nullable(),
  },
);

export const insertOrganizationSchema = createInsertSchema(
  schema.organizations,
  {
    name: z.string().min(1),
    metadata: z.any().nullable(),
    domains: z.array(z.string()).nullable(),
    maxSeats: z.number().min(1),
    customBidTabsMonthlySpendCeilingUsd:
      customBidTabsMonthlySpendCeilingUsdInputSchema.optional(),
    warehouseOwnerIds: z.array(z.string()).nullable(),
  },
);

export const updateOrganizationSchema = insertOrganizationSchema
  .partial()
  .extend({
    id: z.string(),
  });

const OrganizationsQuerySchema = z.object({
  search: z.string().optional(),
  isDemo: z
    .string()
    .optional()
    .transform((val) => {
      if (val === 'true') {
        return true;
      }

      if (val === 'false') {
        return false;
      }

      return undefined;
    }),
});

export const getOrganizationsQuerySchema = OrganizationsQuerySchema.merge(
  z.object({
    limit: z.coerce.number().optional().default(25),
    offset: z.coerce.number().optional().default(0),
    page: z.coerce.number().optional(),
    name: z.string().optional(),
    usedSeats: z
      .string()
      .optional()
      .transform((val) => {
        if (!val) {
          return [];
        }

        // Expect format like "1,5" for range from 1 to 5
        const parts = val.split(',').map((usedSeats) => Number(usedSeats));
        return parts.length === 2 ? parts : [];
      })
      .default([]),
    status: z
      .string()
      .optional()
      .transform((val) => {
        if (!val) {
          return [];
        }

        return val.split(',').map((status) => status);
      })
      .default([]),
    createdAt: z
      .string()
      .optional()
      .transform((val) => {
        if (!val) {
          return [];
        }

        return val.split(',').map((num) => Number(num));
      })
      .default([]),
    sort: z
      .string()
      .optional()
      .transform((val) => {
        if (!val) {
          return [
            {
              id: 'name',
              desc: false,
            },
          ];
        }

        try {
          const parsed = JSON.parse(val);
          const sortSchema = z.array(
            z.object({
              id: z.string(),
              desc: z.boolean(),
            }),
          );

          const result = sortSchema.safeParse(parsed);

          if (!result.success) {
            return [
              {
                id: 'name',
                desc: false,
              },
            ];
          }

          return result.data;
        } catch {
          return [
            {
              id: 'name',
              desc: false,
            },
          ];
        }
      })
      .default([
        {
          id: 'name',
          desc: false,
        },
      ]),
  }),
);

export const getOrganizationMembersQuerySchema = OrganizationsQuerySchema.merge(
  z.object({
    limit: z.coerce.number().optional().default(25),
    offset: z.coerce.number().optional().default(0),
    sortBy: z.enum(['createdAt', 'name']).optional().default('createdAt'),
    page: z.coerce.number().optional(),
    userEmail: z.string().optional(),
    showOwner: z
      .string()
      .optional()
      .transform((val) => val === 'true')
      .default(false),
    showInternal: z
      .string()
      .optional()
      .transform((val) => val === 'true')
      .default(false),
    role: z
      .string()
      .optional()
      .transform((val) => {
        if (!val) {
          return [];
        }

        return val.split(',').map((role) => role);
      })
      .default([]),
    createdAt: z
      .string()
      .optional()
      .transform((val) => {
        if (!val) {
          return [];
        }

        return val.split(',').map((num) => Number(num));
      })
      .default([]),
    sort: z
      .string()
      .optional()
      .transform((val) => {
        if (!val) {
          return [
            {
              id: 'userEmail',
              desc: false,
            },
          ];
        }

        try {
          const parsed = JSON.parse(val);
          const sortSchema = z.array(
            z.object({
              id: z.string(),
              desc: z.boolean(),
            }),
          );

          const result = sortSchema.safeParse(parsed);

          if (!result.success) {
            return [
              {
                id: 'userEmail',
                desc: false,
              },
            ];
          }

          return result.data;
        } catch {
          return [
            {
              id: 'userEmail',
              desc: false,
            },
          ];
        }
      })
      .default([
        {
          id: 'userEmail',
          desc: false,
        },
      ]),
  }),
);

export const getOrganizationsCountQuerySchema = OrganizationsQuerySchema;
